import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import imageUpload from "../../../services/upload";
import {
  FormControl,
  TextField,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import { IMAGE_PATH, instanceToken } from "../../../utils/constant";
import emergencieService from "../../../services/emergencieService";
import {
  EditPageLayout,
  EditImageSection,
  EditFieldLabel,
  EditFormTopSection,
} from "../../../components/edit/EditPageLayout";
import { zodResolver } from "@hookform/resolvers/zod";
import { CreateNewEmergencySchema } from "../../../schema/CreateNewEmergencySchema";
import { useForm } from "react-hook-form";

const imgFileTypes = ["image/gif", "image/jpeg", "image/png", "image/svg+xml"];

export default function UpdateEmergencies({ homeAlert }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const [imageFile, setImageFile] = useState("");
  const [imageFileUrl, setImageFileUrl] = useState("");
  const [values, setValues] = useState({ image_url: "" });
  const [nameEng, setNameEng] = useState("");
  const [nameMM, setNameMM] = useState("");
  const [addressEng, setAddressEng] = useState("");
  const [addressMM, setAddressMM] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneNote, setPhoneNote] = useState("");
  const [category, setCategory] = useState("");
  const [link, setLink] = useState("");
  const [image, setImage] = useState("");
  const [oldImageName, setOldImageName] = useState();
  const [errorMessage, setErrorMessage] = useState({});

  const [emergencies, setEmergencies] = useState(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(CreateNewEmergencySchema),
    initialValues: {
      name_en: "",
      name_mm: "",
      address_en: "",
      address_mm: "",
      phone: "",
      phone_note: "",
      category: "",
      link: "",
    },
  });

  async function getImageUrl(imgData) {
    const response = await imageUpload.getImageUrl(
      instanceToken.token,
      imgData
    );
    const result = response.data;
    const path = IMAGE_PATH + result?.imageName;
    setImageFileUrl(result?.imageUploadUrl);
    setValues({
      ...values,
      image_url: path,
    });
  }
  useEffect(() => {
    if (!emergencies) {
      fetchEmergencies();
    }
  }, []);

  useEffect(() => {
    setNameEng(emergencies?.name_en);
    setNameMM(emergencies?.name_mm);
    setAddressEng(emergencies?.address_en);
    setAddressMM(emergencies?.address_mm);
    setLink(emergencies?.link);
    setPhone(emergencies?.phone);
    setPhoneNote(emergencies?.phone_note);
    setCategory(emergencies?.category);

    if (emergencies) {
      setValue("name_en", emergencies?.name_en);
      setValue("name_mm", emergencies?.name_mm);
      setValue("link", emergencies?.link);
      setValue("address_en", emergencies?.address_en);
      setValue("address_mm", emergencies?.address_mm);
      setValue("phone", emergencies?.phone);
      setValue("phone_note", emergencies?.phone_note);
      setValue("category", emergencies?.category);

      setValues({
        image_url: emergencies?.image_url || "",
      });
    }
  }, [emergencies, setValue]);

  const fetchEmergencies = async () => {
    try {
      const res = await emergencieService.getOneEmergencies(
        instanceToken.token,
        id
      );
      setEmergencies(res.data);
    } catch (error) {
      console.error(error);
      throw new Error(error);
    }
  };
  const changeNameEng = (e) => {
    setNameEng(e.target.value);
  };
  const changeNameMM = (e) => {
    setNameMM(e.target.value);
  };
  const changeAddressEng = (e) => {
    setAddressEng(e.target.value);
  };
  const changeAddressMM = (e) => {
    setAddressMM(e.target.value);
  };
  const changePhone = (e) => {
    setPhone(e.target.value);
  };
  const changePhoneNote = (e) => {
    setPhoneNote(e.target.value);
  };
  const changeLink = (e) => {
    setLink(e.target.value);
  };

  const changeCategory = (e) => {
    setCategory(e.target.value);
  };

  const changeFile = async (e) => {
    if (e.target.files && e.target.files[0]) {
      if (e.target.files && e.target.files[0]) {
        const img = e.target.files[0];
        if (!imgFileTypes.includes(img.type)) {
          setErrorMessage({
            ...errorMessage,
            image_url: "Please select image. (PNG, JPG, JPEG, GIF, ...)",
          });
          return;
        }
        if (img.size > 204800) {
          setErrorMessage({
            ...errorMessage,
            image_url: "Image file size must be smaller than 200KB.",
          });
          return;
        }
        setImageFile(img);
        setImagePreview(URL.createObjectURL(img));
        getImageUrl(img);
      }
    }
  };
  const formData = new FormData();

  const handleUpdate = async (data) => {
    formData.append("name_en", data?.name_en);
    formData.append("name_mm", data?.name_mm);
    formData.append("address_en", data?.address_en);
    formData.append("address_mm", data?.address_mm);
    formData.append("phone", data?.phone);
    formData.append("phone_note", data?.phone_note);
    formData.append("link", data?.link);
    formData.append("category", data?.category);
    formData.append("image_data", "");
    formData.append(
      "image_url",
      values?.image_url ? values?.image_url : data?.image_url
    );

    try {
      setLoading(true);
      if (imageFile) {
        await imageUpload.uploadImage(imageFileUrl, imageFile);
      }
      const response = await emergencieService.putEmergencies(
        instanceToken.token,
        id,
        formData
      );
      navigate("/emergencies");
      setEmergencies(response.data);
      homeAlert("Emergencies have been updated.", false);
    } catch (error) {
      console.log(error);
      homeAlert("Error on server.", true);
    } finally {
      setLoading(false);
      setErrorMessage({});
    }
  };

  const handleRemove = async () => {
    try {
      await emergencieService.deleteEmergencies(instanceToken.token, id);
      homeAlert("Emergencies post have been removed.", false);
      navigate("/emergencies");
    } catch (error) {
      console.log(error);
      homeAlert("Error on server!", true);
    }
  };

  return (
    <EditPageLayout
      title="Edit Emergency"
      subtitle="Update emergency contact details and information."
      breadcrumbs={[
        { label: "Emergencies", path: "/emergencies" },
        { label: `Emergency (ID - ${id})`, path: `/emergencies/${id}` },
        { label: "Edit" },
      ]}
      detailPath={`/emergencies/${id}`}
      onRemove={handleRemove}
      loading={loading}
      onSubmit={handleSubmit(handleUpdate)}
      removeMessage="Are you sure you want to remove this emergency contact?"
    >
      <EditFormTopSection
        image={
          <EditImageSection
            uploadLabel="Emergency Image"
            previewLabel="Image Preview"
            imageUrl={values?.image_url}
            imagePreview={imagePreview}
            onFileChange={changeFile}
            error={errorMessage["image_url"]}
            recommendedSize="340 x 180 px (1.8:1)"
          />
        }
      >
        <div>
          <EditFieldLabel required>Name (English)</EditFieldLabel>
          <TextField
            fullWidth
            id="name_en"
            {...register("name_en")}
            onChange={changeNameEng}
            value={nameEng}
            error={!!errors.name_en}
            helperText={errors.name_en?.message}
          />
        </div>
        <div>
          <EditFieldLabel required>Name (Myanmar)</EditFieldLabel>
          <TextField
            fullWidth
            id="name_mm"
            {...register("name_mm")}
            onChange={changeNameMM}
            value={nameMM}
            error={!!errors.name_mm}
            helperText={errors.name_mm?.message}
          />
        </div>
        <div>
          <EditFieldLabel required>Address (English)</EditFieldLabel>
          <TextField
            fullWidth
            id="address_en"
            {...register("address_en")}
            onChange={changeAddressEng}
            value={addressEng}
            error={!!errors.address_en}
            helperText={errors.address_en?.message}
          />
        </div>
        <div>
          <EditFieldLabel required>Address (Myanmar)</EditFieldLabel>
          <TextField
            fullWidth
            id="address_mm"
            {...register("address_mm")}
            onChange={changeAddressMM}
            value={addressMM}
            error={!!errors.address_mm}
            helperText={errors.address_mm?.message}
          />
        </div>
        <div>
          <EditFieldLabel required>Phone</EditFieldLabel>
          <TextField
            fullWidth
            id="phone"
            {...register("phone")}
            onChange={changePhone}
            value={phone}
            error={!!errors.phone}
            helperText={errors.phone?.message}
          />
        </div>
        <div>
          <EditFieldLabel>Phone Note</EditFieldLabel>
          <TextField
            fullWidth
            id="phone_note"
            {...register("phone_note")}
            onChange={changePhoneNote}
            value={phoneNote}
            error={!!errors.phone_note}
            helperText={errors.phone_note?.message}
          />
        </div>
        <div>
          <EditFieldLabel required>Category</EditFieldLabel>
          <FormControl fullWidth error={!!errors.category}>
            <Select value={category} onChange={changeCategory} displayEmpty>
              <MenuItem value="" disabled>
                Select category
              </MenuItem>
              <MenuItem value="Emergency Hotline">Emergency Hotline</MenuItem>
              <MenuItem value="Ambulance">Ambulance</MenuItem>
              <MenuItem value="Oxygen">Oxygen</MenuItem>
              <MenuItem value="Blood Donation">Blood Donation</MenuItem>
            </Select>
            <FormHelperText error>
              {errors.category?.message || errorMessage["category"]}
            </FormHelperText>
          </FormControl>
        </div>
        <div>
          <EditFieldLabel>Link</EditFieldLabel>
          <TextField
            fullWidth
            id="link"
            {...register("link")}
            onChange={changeLink}
            value={link}
            error={!!errors.link}
            helperText={errors.link?.message}
          />
        </div>
      </EditFormTopSection>
    </EditPageLayout>
  );
}
